import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Sparkles, Wand2, Loader2, ChevronLeft, Check, HelpCircle, Send, Plug, RefreshCw, TestTube, CheckCircle2, AlertCircle } from 'lucide-react';
import { LiquidLoader } from '../components/LiquidLoader.js';
import { haptic } from '../../telegram';
import {
  marketplaceApi,
  type OrchestratorQuestion,
  type OrchestratedManifestResult,
  type SetupAssistantMessage,
} from '../../api/marketplaceApi';
import { integrationsApi } from '../../api/integrationsApi';
import { feedbackApi } from '../../api/feedbackApi';

/*
  Конструктор автоматизаций — отдельное окно (не чат).
  Шаги: описание → уточняющие вопросы (orchestrate-clarify) →
  сборка плана (orchestrate-manifest) → резюме и подтверждение (install-manifest).
  Создание через чат (ТГ / обычный чат) продолжает работать через оркестратора.
*/

const EXAMPLES = [
  'Каждое утро в 9:00 присылай мне сводку задач и важных писем на день',
  'Когда приходит заявка с формы — отвечай клиенту и сохраняй её в базу',
  'Раз в неделю собирай отчёт по продажам и публикуй в Telegram-канал',
];

type Step = 'describe' | 'connect' | 'clarify' | 'building' | 'review';

function humanSchedule(cron?: string): string {
  const m = /^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+\*$/.exec(String(cron || '').trim());
  if (m) {
    const hh = String(Number(m[2])).padStart(2, '0');
    const mm = String(Number(m[1])).padStart(2, '0');
    return 'Каждый день в ' + hh + ':' + mm;
  }
  return 'По расписанию';
}

function composePrompt(base: string, questions: OrchestratorQuestion[], answers: Record<string, string>): string {
  const lines = [base.trim()];
  for (const q of questions) {
    if (answers[q.id]) lines.push(q.text + ' — ' + answers[q.id]);
  }
  return lines.join('\n');
}

interface Preview { title: string; emoji: string; description: string; when: string; sendsTo: string; tools: string[]; }

function previewFromManifest(manifest: any): Preview {
  const agent = (manifest?.agents?.[0] ?? {}) as Record<string, any>;
  const tools = Array.isArray(agent.enabled_builtins) ? agent.enabled_builtins.map(String) : [];
  const title = String(agent.name || 'Новая автоматизация');
  const description = String(agent.description || 'Готово к запуску.');
  const trigger = manifest?.trigger as Record<string, any> | undefined;
  let when = 'Запуск вручную или из чата';
  if (trigger?.type === 'schedule') when = humanSchedule(trigger.cron);
  else if (trigger?.type === 'inbound_webhook') when = 'При входящем событии';
  const target = (manifest?.response?.target as string | undefined) ?? 'telegram';
  const sendsTo =
    target === 'telegram' ? 'Пришлёт в Telegram'
    : target === 'both' ? 'В Telegram и во внешний сервис'
    : target === 'outbound_webhook' ? 'Во внешний сервис'
    : target === 'none' ? 'Сохранит без отправки'
    : 'Ответит в чат';
  return { title, emoji: '⚙️', description, when, sendsTo, tools };
}

function inputType(type: string): string {
  if (type === 'secret' || type === 'byok') return 'password';
  if (type === 'number') return 'number';
  if (type === 'url') return 'url';
  return 'text';
}

const kvStyle: React.CSSProperties = { fontSize: 14, lineHeight: 1.5, marginBottom: 6 };
const titleStyle: React.CSSProperties = { fontWeight: 700, fontSize: 15, marginBottom: 8 };
const chipsTopStyle: React.CSSProperties = { marginTop: 10 };
const hintTopStyle: React.CSSProperties = { marginTop: 10 };
const errStyle: React.CSSProperties = { color: 'var(--rf-red)', marginTop: 12 };

// Подсказка "Настройте под себя": компактный сворачиваемый мини-чат,
// который сразу знает контекст автоматизации и список полей для заполнения.
const helpToggleStyle: React.CSSProperties = { width: '100%', justifyContent: 'center', marginTop: 14 };
const helpPanelStyle: React.CSSProperties = { marginTop: 10, display: 'flex', flexDirection: 'column' };
const helpMsgsStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto', padding: '2px 2px 6px' };
const helpBubbleBaseStyle: React.CSSProperties = { padding: '8px 12px', borderRadius: 14, fontSize: 13.5, lineHeight: 1.45, wordBreak: 'break-word', maxWidth: '88%' };
const helpBubbleUserStyle: React.CSSProperties = { ...helpBubbleBaseStyle, alignSelf: 'flex-end', background: 'var(--rf-accent-grad)', color: '#fff', borderBottomRightRadius: 4, whiteSpace: 'pre-wrap' };
const helpBubbleAssistantStyle: React.CSSProperties = { ...helpBubbleBaseStyle, alignSelf: 'flex-start', background: 'var(--rf-surface-2)', border: '1px solid var(--rf-border-soft)', color: 'var(--rf-text)', borderBottomLeftRadius: 4 };
const helpRowStyle: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 };

// Мини-чат не умеет рендерить HTML/Markdown "из коробки", а подсказка иногда
// отвечает с markdown-разметкой (заголовки, **жирный**, `код`, списки).
// Чтобы пользователь не видел решётки/звёздочки как есть — разбираем самые
// частые элементы разметки руками и рисуем их обычными элементами интерфейса.
const miniHeadingStyle: React.CSSProperties = { fontWeight: 700, marginTop: 4, marginBottom: 2 };
const miniParaStyle: React.CSSProperties = { marginBottom: 4 };
const miniListStyle: React.CSSProperties = { margin: '2px 0 4px 18px', padding: 0 };
const inlineCodeStyle: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: 'rgba(127,127,127,0.16)', padding: '1px 5px', borderRadius: 5, fontSize: '0.92em' };

function renderInlineMd(text: string, keyPrefix: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter((p) => p !== '');
  if (parts.length === 0) return [text];
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return <b key={keyPrefix + '-b-' + i}>{part.slice(2, -2)}</b>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      return <code key={keyPrefix + '-c-' + i} style={inlineCodeStyle}>{part.slice(1, -1)}</code>;
    }
    return <React.Fragment key={keyPrefix + '-t-' + i}>{part}</React.Fragment>;
  });
}

function MiniMarkdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  let listOrdered = false;
  const flushList = (key: string) => {
    if (listBuffer.length === 0) return;
    const items = listBuffer;
    blocks.push(
      listOrdered
        ? <ol key={key} style={miniListStyle}>{items.map((item, i) => (<li key={key + '-li-' + i}>{renderInlineMd(item, key + '-' + i)}</li>))}</ol>
        : <ul key={key} style={miniListStyle}>{items.map((item, i) => (<li key={key + '-li-' + i}>{renderInlineMd(item, key + '-' + i)}</li>))}</ul>
    );
    listBuffer = [];
  };
  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    const key = 'l' + idx;
    if (!line || /^-{3,}$/.test(line)) { flushList(key); return; }
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      flushList(key);
      blocks.push(<div key={key} style={miniHeadingStyle}>{renderInlineMd(heading[1], key)}</div>);
      return;
    }
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      if (listOrdered) flushList(key);
      listOrdered = false;
      listBuffer.push(bullet[1]);
      return;
    }
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (numbered) {
      if (!listOrdered) flushList(key);
      listOrdered = true;
      listBuffer.push(numbered[1]);
      return;
    }
    flushList(key);
    blocks.push(<div key={key} style={miniParaStyle}>{renderInlineMd(line, key)}</div>);
  });
  flushList('end');
  return <>{blocks}</>;
}

export function AutomationWizard() {
  const nav = useNavigate();
  // Восстанавливаем состояние из localStorage — навигация в «Связи» и назад не теряет прогресс.
  const _ws = ((): any => { try { return JSON.parse(localStorage.getItem('wizard_state_v2') || 'null'); } catch { return null; } })();
  // «Хочу так же» с карточки маркетплейса: приходим сюда с готовым описанием сценария.
  // Сид имеет приоритет над сохранённым прогрессом — иначе пользователь увидит чужой черновик.
  const loc = useLocation();
  const seedPrompt = typeof (loc.state as any)?.seedPrompt === 'string' ? String((loc.state as any).seedPrompt).trim() : '';
  const [step, setStep] = useState<Step>(seedPrompt ? 'describe' : (_ws?.step ?? 'describe'));
  const [desc, setDesc] = useState<string>(seedPrompt || (_ws?.desc ?? ''));
  useEffect(() => {
    if (!seedPrompt) return;
    // Сид одноразовый: чистим сохранённый прогресс и убираем state из истории,
    // чтобы возврат «назад» не подставил то же описание поверх правок пользователя.
    localStorage.removeItem('wizard_state_v2');
    nav(loc.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedPrompt]);
  const [questions, setQuestions] = useState<OrchestratorQuestion[]>(_ws?.questions ?? []);
  const [stepIndex, setStepIndex] = useState<number>(_ws?.stepIndex ?? 0);
  const [answers, setAnswers] = useState<Record<string, string>>(_ws?.answers ?? {});
  const [draft, setDraft] = useState<OrchestratedManifestResult | null>(_ws?.draft ?? null);
  const [inputs, setInputs] = useState<Record<string, string>>(_ws?.inputs ?? {});
  const [accountSelections, setAccountSelections] = useState<Record<string, string>>(_ws?.accountSelections ?? {});
  const [busy, setBusy] = useState(false);
  // Собственный бот — встроенная форма (без перехода в «Связи»).
  const [tgOwnOpen, setTgOwnOpen] = useState(false);
  const [tgToken, setTgToken] = useState('');
  const [tgOwnBusy, setTgOwnBusy] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [pendingConnect, setPendingConnect] = useState<Array<{ id: string; name: string }>>(_ws?.pendingConnect ?? []);
  const [checkingConnect, setCheckingConnect] = useState<string | null>(null);
  const [connectTestResults, setConnectTestResults] = useState<Record<string, { ok: boolean; msg: string } | undefined>>({});

  // Состояние мини-чата "подсказка" в шаге review.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpStarted, setHelpStarted] = useState(false);
  const [helpMessages, setHelpMessages] = useState<SetupAssistantMessage[]>([]);
  const [helpInput, setHelpInput] = useState('');
  const [helpBusy, setHelpBusy] = useState(false);

  // Сброс визарда: вместо голого confirm — панель с вопросом «что не понравилось».
  const [dropOpen, setDropOpen] = useState(false);
  const [dropComment, setDropComment] = useState('');
  const [dropBusy, setDropBusy] = useState(false);

  const build = async (qs: OrchestratorQuestion[], ans: Record<string, string>) => {
    setError(null); setBusy(true); setStep('building');
    try {
      const finalPrompt = composePrompt(desc, qs, ans);
      const res = await marketplaceApi.orchestrateManifest({ prompt: finalPrompt, kind: 'pipeline' });
      const nextInputs: Record<string, string> = {};
      for (const r of res.requirements ?? []) {
        if (r.default !== null && r.default !== undefined) nextInputs[r.ref] = String(r.default);
      }
      setDraft(res); setInputs(nextInputs); setAccountSelections({}); setStep('review'); haptic('medium'); localStorage.removeItem('wizard_state_v2');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось собрать план');
      setStep(qs.length ? 'clarify' : 'describe');
    } finally { setBusy(false); }
  };

  const start = async () => {
    if (!desc.trim()) { setError('Опишите, что нужно сделать'); return; }
    setError(null); setBusy(true);
    try {
      const res = await marketplaceApi.orchestrateClarify({ prompt: desc.trim(), kind: 'pipeline' });
      const hasQuestions = res.status === 'questions' && res.questions.length > 0;
      setQuestions(hasQuestions ? res.questions : []); setStepIndex(0); setAnswers({});
      const pending = res.pendingServices ?? [];
      setPendingConnect(pending);
      if (pending.length > 0) {
        setStep('connect');
      } else if (hasQuestions) {
        setStep('clarify'); haptic('light');
      } else {
        await build([], {});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помощник временно недоступен');
    } finally { setBusy(false); }
  };

  const proceedAfterConnect = async () => {
    haptic('light');
    setError(null); setBusy(true);
    try {
      const res = await marketplaceApi.orchestrateClarify({ prompt: desc.trim(), kind: 'pipeline' });
      const hasQuestions = res.status === 'questions' && res.questions.length > 0;
      const pending = res.pendingServices ?? [];
      setPendingConnect(pending);
      setQuestions(hasQuestions ? res.questions : []); setStepIndex(0); setAnswers({});
      if (pending.length > 0) {
        return;
      }
      if (hasQuestions) { setStep('clarify'); haptic('light'); }
      else { await build([], {}); }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помощник временно недоступен');
    } finally { setBusy(false); }
  };

  const current = questions[stepIndex];
  const pickAnswer = (value: string) => { if (!current) return; setAnswers((p) => ({ ...p, [current.id]: value })); haptic('light'); };
  const goNext = () => { if (stepIndex < questions.length - 1) setStepIndex((i) => i + 1); else void build(questions, answers); };
  const resetWizard = () => {
    localStorage.removeItem('wizard_state_v2');
    setStep('describe'); setDesc(''); setQuestions([]); setStepIndex(0);
    setAnswers({}); setDraft(null); setInputs({}); setAccountSelections({});
    setPendingConnect([]); setError(null); setBusy(false);
    setTgOwnOpen(false); setTgToken('');
  };
  // Лог брошенного визарда уходит всегда, комментарий — по желанию.
  // Сбой отправки не должен мешать человеку начать заново.
  const confirmDrop = async (comment: string) => {
    setDropBusy(true);
    try {
      await feedbackApi.wizardDrop({
        step,
        prompt: desc.trim(),
        comment: comment.trim(),
        questionsAnswered: Object.keys(answers).length,
        questionsTotal: questions.length,
        lastError: error ?? undefined,
      });
    } catch {
      /* молча: сброс важнее лога */
    } finally {
      setDropBusy(false);
      setDropOpen(false);
      setDropComment('');
      resetWizard();
      haptic('medium');
    }
  };
  const goBack = () => { if (stepIndex > 0) setStepIndex((i) => i - 1); else setStep(pendingConnect.length > 0 ? 'connect' : 'describe'); };

  const install = async () => {
    if (!draft) return;
    setError(null);
    for (const r of draft.requirements ?? []) {
      const v = String(inputs[r.ref] ?? '').trim();
      if (r.required && !v && (r.default === null || r.default === undefined)) { setError('Заполните: ' + r.label); return; }
    }
    for (const c of draft.accountChoices ?? []) {
      if (!accountSelections[c.provider]) { setError('Выберите аккаунт: ' + c.providerLabel); return; }
    }
    const preview = previewFromManifest(draft.manifest);
    setInstalling(true);
    try {
      const res = await marketplaceApi.installManifest({ manifest: draft.manifest, inputs, title: preview.title, summary: preview.description, accountSelections });
      haptic('medium'); localStorage.removeItem('wizard_state_v2');
      if (res.pipelineId) nav('/automation/' + res.pipelineId);
      else nav('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка создания');
    } finally { setInstalling(false); }
  };

  // Подключение недостающего сервиса прямо из визарда (OAuth открывается во внешнем окне).
  const openExtLink = (url: string) => { const tg = (window as any)?.Telegram?.WebApp; if (tg?.openLink) tg.openLink(url); else window.open(url, '_blank'); };
  const connectPending = async (id: string) => {
    haptic('medium'); setConnecting(id); setError(null);
    try {
      const r = await integrationsApi.connectProvider(id);
      if (r?.mode === 'oauth' && r.authorizeUrl) openExtLink(r.authorizeUrl);
      else haptic('heavy');
    } catch (e) { setError(e instanceof Error ? e.message : 'Не удалось начать подключение'); }
    finally { setConnecting(null); }
  };
  const connectTelegramPlatform = async () => {
    haptic('medium'); setConnecting('telegram'); setError(null);
    try {
      const r = await integrationsApi.tgConnectPlatform();
      if (r?.deepLink) openExtLink(r.deepLink);
    } catch (e) { setError(e instanceof Error ? e.message : 'Не удалось начать подключение'); }
    finally { setConnecting(null); }
  };
  // Сохраняем состояние конструктора в localStorage при каждом изменении.
  useEffect(() => {
    if (busy || (step === 'describe' && !desc.trim() && !draft)) { localStorage.removeItem('wizard_state_v2'); return; }
    localStorage.setItem('wizard_state_v2', JSON.stringify({ step, desc, questions, stepIndex, answers, draft, inputs, accountSelections, pendingConnect }));
  }, [step, desc, questions, stepIndex, answers, draft, inputs, accountSelections, pendingConnect]);

  const connectOwnBot = async () => {
    if (!tgToken.trim()) return;
    haptic('medium'); setTgOwnBusy(true); setError(null);
    try {
      await integrationsApi.tgConnectCustom(tgToken.trim());
      setTgToken(''); setTgOwnOpen(false);
      // Перепроверка подключения сразу после добавления бота.
      const ov = await integrationsApi.tgOverview();
      const ready = (ov.resources || []).length;
      if (ready > 0) { setConnectTestResults((p) => ({ ...p, telegram: { ok: true, msg: 'Подключено каналов: ' + ready } })); haptic('heavy'); setPendingConnect((p) => p.filter((x) => x.id !== 'telegram')); }
      else setConnectTestResults((p) => ({ ...p, telegram: { ok: false, msg: 'Бот подключён. Добавьте его в канал и нажмите «Проверить».' } }));
    } catch (e) { setError(e instanceof Error ? e.message : 'Не удалось подключить бота'); }
    finally { setTgOwnBusy(false); }
  };

  const checkConnectStatus = async (id: string) => {
    if (id === 'telegram') {
      haptic('light'); setCheckingConnect(id); setConnectTestResults((p) => ({ ...p, [id]: undefined }));
      try {
        const ov = await integrationsApi.tgOverview();
        const ready = (ov.resources || []).length;
        if (ready > 0) { setConnectTestResults((p) => ({ ...p, [id]: { ok: true, msg: 'Подключено каналов: ' + ready } })); haptic('heavy'); setPendingConnect((p) => p.filter((x) => x.id !== id)); }
        else setConnectTestResults((p) => ({ ...p, [id]: { ok: false, msg: 'Каналы не найдены. Добавьте бота админом в канал.' } }));
      } catch (e) { setConnectTestResults((p) => ({ ...p, [id]: { ok: false, msg: e instanceof Error ? e.message : 'Ошибка' } })); }
      finally { setCheckingConnect(null); }
      return;
    }
    haptic('light'); setCheckingConnect(id); setConnectTestResults((p) => ({ ...p, [id]: undefined }));
    try {
      const catalog = await integrationsApi.getCatalog();
      const provider = catalog.providers.find((p) => p.id === id);
      const conn = provider?.connection || (provider?.connections && provider.connections[0]) || null;
      if (!conn) {
        setConnectTestResults((p) => ({ ...p, [id]: { ok: false, msg: 'Ещё не подключено' } }));
        return;
      }
      const r = await integrationsApi.testConnection(conn.id);
      const count = (r.toolDetails || r.tools || []).length;
      const msg = r.ok ? ('Подключено, доступно инструментов: ' + count + (r.demo ? ' (демо)' : '')) : (r.error || 'Ошибка подключения');
      setConnectTestResults((p) => ({ ...p, [id]: { ok: r.ok, msg } }));
      if (r.ok) { haptic('heavy'); setPendingConnect((p) => p.filter((s) => s.id !== id)); }
    } catch (e) {
      setConnectTestResults((p) => ({ ...p, [id]: { ok: false, msg: e instanceof Error ? e.message : 'Ошибка' } }));
    } finally { setCheckingConnect(null); }
  };
  // После подключения перегенерируем манифест — теперь сервис автоматически привяжется.
  const rebuildPlan = () => { void build(questions, answers); };

  // Подсказка "Настройте под себя": передаёт ассистенту контекст автоматизации
  // (название, описание, список полей) и историю мини-чата, получает ответ LLM.
  const askSetupAssistant = async (history: SetupAssistantMessage[]) => {
    const ctxPreview = draft ? previewFromManifest(draft.manifest) : null;
    const ctxRequirements = draft?.requirements ?? [];
    return marketplaceApi.setupAssistant({
      automationTitle: ctxPreview?.title || 'Автоматизация',
      automationSummary: ctxPreview?.description,
      requirements: ctxRequirements.map((r) => ({ ref: r.ref, label: r.label, type: r.type, description: r.description ?? null })),
      messages: history,
    });
  };

  const toggleHelp = async () => {
    haptic('light');
    setHelpOpen((v) => !v);
    if (!helpStarted) {
      setHelpStarted(true);
      setHelpBusy(true);
      try {
        const res = await askSetupAssistant([]);
        setHelpMessages([{ role: 'assistant', content: res.reply }]);
      } catch (e) {
        setHelpMessages([{ role: 'assistant', content: 'Не удалось загрузить подсказку. Напишите, с каким сервисом нужна помощь, и я попробую снова.' }]);
      } finally {
        setHelpBusy(false);
      }
    }
  };

  const sendHelp = async () => {
    const text = helpInput.trim();
    if (!text || helpBusy) return;
    const next = [...helpMessages, { role: 'user' as const, content: text }];
    setHelpMessages(next);
    setHelpInput('');
    setHelpBusy(true);
    try {
      const res = await askSetupAssistant(next);
      setHelpMessages((p) => [...p, { role: 'assistant' as const, content: res.reply }]);
    } catch (e) {
      setHelpMessages((p) => [...p, { role: 'assistant' as const, content: 'Не удалось получить ответ. Попробуйте ещё раз.' }]);
    } finally {
      setHelpBusy(false);
    }
  };

  const preview = draft ? previewFromManifest(draft.manifest) : null;
  const requirements = draft?.requirements ?? [];

  const onTopBack = () => {
    haptic('light');
    if (step === 'review') setStep(questions.length ? 'clarify' : (pendingConnect.length ? 'connect' : 'describe'));
    else if (step === 'clarify') goBack();
    else if (step === 'connect') setStep('describe');
    else nav(-1 as any);
  };

  const dropCardStyle: React.CSSProperties = { marginBottom: 12 };
  const ownBotFieldStyle: React.CSSProperties = { width: '100%', marginTop: 8 };
  const ownBotBtnRowStyle: React.CSSProperties = { marginTop: 6, display: 'flex', gap: 6 };
  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={onTopBack}><ArrowLeft />Назад</button>
        {(step !== 'describe' || busy) && <button className="rf-btn sm" onClick={() => { haptic('light'); setDropOpen(true); }}>Сбросить</button>}
        <div className="rf-topbar-title">Конструктор автоматизаций</div>
      </div>

      <div className="rf-detail-scroll">
        {dropOpen && (
          <div className="rf-card" style={dropCardStyle}>
            <div style={titleStyle}>Начать заново?</div>
            <div className="rf-hint">Собранный прогресс очистится. Если что-то не получилось или было непонятно — напишите в двух словах. Уйдёт напрямую разработчику.</div>
            <div className="rf-field" style={chipsTopStyle}>
              <textarea className="rf-textarea" value={dropComment} onChange={(e) => setDropComment(e.target.value)} placeholder="Например: не понял, что писать в вопросе про расписание" rows={3} />
            </div>
            <div className="rf-btn-row">
              <button className="rf-btn primary" onClick={() => { haptic('light'); void confirmDrop(dropComment); }} disabled={dropBusy || !dropComment.trim()}>{dropBusy ? <Loader2 className="spin" /> : <Send />}Отправить и сбросить</button>
              <button className="rf-btn" onClick={() => { haptic('light'); void confirmDrop(''); }} disabled={dropBusy}>Просто сбросить</button>
            </div>
            <button className="rf-btn ghost block" style={hintTopStyle} onClick={() => { haptic('light'); setDropOpen(false); setDropComment(''); }} disabled={dropBusy}>Отмена</button>
          </div>
        )}
        {!dropOpen && step === 'describe' && (
          <>
            <div className="rf-info"><Wand2 /><div><b>Сборка с оркестратором</b><br />Опишите своими словами, что нужно автоматизировать. Оркестратор задаст уточняющие вопросы и покажет резюме — вы подтвердите перед созданием.</div></div>
            <div className="rf-field"><label>Что должна делать автоматизация?</label>
              <textarea className="rf-textarea" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Например: каждое утро присылай мне сводку задач" rows={4} />
            </div>
            <div className="rf-seclabel">Примеры</div>
            <div className="rf-wiz-ex">
              {EXAMPLES.map((ex) => (<button key={ex} onClick={() => { haptic('light'); setDesc(ex); }}>{ex}</button>))}
            </div>
            <div className="rf-btn-row">
              <button className="rf-btn primary" onClick={start} disabled={!desc.trim() || busy}>{busy ? <Loader2 className="spin" /> : <Sparkles />}Начать сборку</button>
            </div>
          </>
        )}

        {!dropOpen && step === 'connect' && (
          <>
            <div className="rf-info"><Plug /><div><b>Нужно подключить сервис</b><br />Сначала подключите сервисы, потом нажмите «Проверить» и «Продолжить» — оркестратор задаст уточняющие вопросы уже зная ваши инструменты и ресурсы.</div></div>
            {pendingConnect.map((s) => { const res = connectTestResults[s.id]; const isTg = s.id === 'telegram'; return (
              <div className="rf-field" key={s.id}>
                <div className="rf-conn-row">
                  <div className="rf-conn-info"><div className="rf-conn-name">{s.name}</div></div>
                  {isTg ? (
                    <button className="rf-btn sm primary" onClick={() => void connectTelegramPlatform()} disabled={connecting === s.id}>{connecting === s.id ? <Loader2 className="spin" /> : <Plug />}Через бота платформы</button>
                  ) : (
                    <button className="rf-btn sm primary" onClick={() => void connectPending(s.id)} disabled={connecting === s.id}>{connecting === s.id ? <Loader2 className="spin" /> : <Plug />}Подключить</button>
                  )}
                </div>
                {isTg && <div className="rf-hint">Добавьте бота админом в канал — он появится сам. Или подключите своего бота в разделе «Связи».</div>}
                <div className="rf-acts">
                  {isTg && (
                    <>
                      <button className="rf-btn sm" onClick={() => { haptic('light'); setTgOwnOpen((o) => !o); setTgToken(''); }}>{tgOwnOpen ? 'Свой бот ✕' : 'Свой бот'}</button>
                      {tgOwnOpen && (
                        <div className="rf-field" style={ownBotFieldStyle}>
                          <label>Токен бота (@BotFather)</label>
                          <input className="rf-input" type="password" value={tgToken} onChange={(e) => setTgToken(e.target.value)} placeholder="123456789:ABC-DEF…" />
                          <div className="rf-btn-row" style={ownBotBtnRowStyle}>
                            <button className="rf-btn sm primary" onClick={() => void connectOwnBot()} disabled={tgOwnBusy || !tgToken.trim()}>{tgOwnBusy ? <Loader2 className="spin" /> : null}Подключить</button>
                            <button className="rf-btn sm" onClick={() => { setTgOwnOpen(false); setTgToken(''); }}>Отмена</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <button className="rf-btn sm rf-grow" onClick={() => void checkConnectStatus(s.id)} disabled={checkingConnect === s.id}>{checkingConnect === s.id ? <Loader2 className="spin" /> : <TestTube />}Проверить</button>
                </div>
                {res && (<div className={'rf-result ' + (res.ok ? 'ok' : 'err')}>{res.ok ? <CheckCircle2 /> : <AlertCircle />}<div>{res.msg}</div></div>)}
              </div>
            ); })}
            <div className="rf-btn-row">
              <button className="rf-btn primary block" onClick={() => void proceedAfterConnect()} disabled={busy}>{busy ? <Loader2 className="spin" /> : null}Продолжить</button>
            </div>
          </>
        )}

        {!dropOpen && step === 'clarify' && current && (
          <>
            <div className="rf-seclabel">Уточнение {stepIndex + 1} из {questions.length}</div>
            <div className="rf-info"><div><b>{current.text}</b></div></div>
            <div className="rf-chipgrid">
              {current.options.map((o) => (
                <button key={o} className={'rf-chiptog' + (answers[current.id] === o ? ' on' : '')} onClick={() => pickAnswer(o)}>{answers[current.id] === o ? <Check /> : null}{o}</button>
              ))}
            </div>
            <div className="rf-seclabel" style={chipsTopStyle}>Свой вариант</div>
            <input className="rf-input" value={answers[current.id] ?? ''} onChange={(e) => setAnswers((prev) => ({ ...prev, [current.id]: e.target.value }))} placeholder="Введите свой ответ" />
            <div className="rf-btn-row">
              <button className="rf-btn" onClick={goBack}><ChevronLeft />Назад</button>
              <button className="rf-btn primary" onClick={goNext} disabled={!answers[current.id] || busy}>{busy ? <Loader2 className="spin" /> : null}{stepIndex < questions.length - 1 ? 'Далее' : 'Собрать план'}</button>
            </div>
          </>
        )}

        {!dropOpen && step === 'building' && (
          <LiquidLoader />
        )}

        {!dropOpen && step === 'review' && draft && preview && (
          <>
            <div className="rf-info"><Sparkles /><div><b>Вот что я сделаю</b><br />Проверьте и подтвердите — автоматизация создастся только после нажатия «Создать».</div></div>
            <div className="rf-card rf-cardpad">
              <div style={titleStyle}>{preview.emoji} {preview.title}</div>
              <div style={kvStyle}><b>Что:</b> {preview.description}</div>
              <div style={kvStyle}><b>Когда:</b> {preview.when}</div>
              <div style={kvStyle}><b>Куда:</b> {preview.sendsTo}</div>
              {preview.tools.length > 0 && (
                <div className="rf-chipgrid" style={chipsTopStyle}>
                  {preview.tools.map((t) => (<span key={t} className="rf-toolchip">{t}</span>))}
                </div>
              )}
            </div>
            {draft.source === 'fallback' && (
              <div className="rf-hint" style={hintTopStyle}>Это черновик по шаблону — проверьте детали внимательнее.</div>
            )}
            {draft.pendingServices && draft.pendingServices.length > 0 && (
              <>
                <div className="rf-seclabel">Нужно подключить</div>
                <div className="rf-hint rf-mb12">Для этой автоматизации нужны сервисы, которые ещё не подключены. Подключите их, затем нажмите «Пересобрать план».</div>
                {draft.pendingServices.map((s) => (
                  <div className="rf-field" key={s.id}>
                    <div className="rf-conn-row">
                      <div className="rf-conn-info"><div className="rf-conn-name">{s.name}</div></div>
                      <button className="rf-btn sm primary" onClick={() => void (s.id === 'telegram' ? connectTelegramPlatform() : connectPending(s.id))} disabled={connecting === s.id}>{connecting === s.id ? <Loader2 className="spin" /> : <Plug />}Подключить</button>
                    </div>
                  </div>
                ))}
                <button className="rf-btn" style={helpToggleStyle} onClick={() => { haptic('light'); rebuildPlan(); }} disabled={busy}>{busy ? <Loader2 className="spin" /> : <RefreshCw />}Пересобрать план</button>
              </>
            )}
            {draft.accountChoices && draft.accountChoices.length > 0 && (
              <>
                <div className="rf-seclabel">Какой аккаунт использовать</div>
                <div className="rf-hint rf-mb12">У этого сервиса несколько подключённых аккаунтов — выберите, с каким работать.</div>
                {draft.accountChoices.map((c) => (
                  <div className="rf-field" key={c.provider}>
                    <label>{c.providerLabel}</label>
                    <div className="rf-chipgrid">
                      {c.options.map((o) => (
                        <button key={o.id} className={'rf-chiptog' + (accountSelections[c.provider] === o.id ? ' on' : '')} onClick={() => { haptic('light'); setAccountSelections((pp) => ({ ...pp, [c.provider]: o.id })); }}>{accountSelections[c.provider] === o.id ? <Check /> : null}{o.label}</button>
                      ))}
                      <button className={'rf-chiptog' + (accountSelections[c.provider] === '__all__' ? ' on' : '')} onClick={() => { haptic('light'); setAccountSelections((pp) => ({ ...pp, [c.provider]: '__all__' })); }}>{accountSelections[c.provider] === '__all__' ? <Check /> : null}Все аккаунты</button>
                    </div>
                  </div>
                ))}
              </>
            )}
            {requirements.length > 0 && (
              <>
                <div className="rf-seclabel">Настройте под себя</div>
                {requirements.map((r) => (
                  <div className="rf-field" key={r.ref}>
                    <label>{r.required ? r.label + ' *' : r.label}</label>
                    <input className="rf-input" type={inputType(r.type)} value={inputs[r.ref] ?? ''} placeholder={r.default == null ? '' : String(r.default)} onChange={(e) => setInputs((p) => ({ ...p, [r.ref]: e.target.value }))} />
                    {r.description && <div className="rf-hint">{r.description}</div>}
                  </div>
                ))}
                <button className="rf-btn" style={helpToggleStyle} onClick={() => void toggleHelp()}>
                  <HelpCircle />{helpOpen ? 'Свернуть подсказку' : 'Не понимаю, что вводить — подсказка'}
                </button>
                {helpOpen && (
                  <div className="rf-card rf-cardpad" style={helpPanelStyle}>
                    <div style={helpMsgsStyle}>
                      {helpMessages.map((m, i) => (
                        <div key={i} style={m.role === 'user' ? helpBubbleUserStyle : helpBubbleAssistantStyle}>
                          {m.role === 'assistant' ? <MiniMarkdown text={m.content} /> : m.content}
                        </div>
                      ))}
                      {helpBusy && <div style={helpBubbleAssistantStyle}><Loader2 className="spin" /></div>}
                    </div>
                    <div style={helpRowStyle}>
                      <input
                        className="rf-input"
                        value={helpInput}
                        onChange={(e) => setHelpInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void sendHelp(); }}
                        placeholder="Например: я использую Notion"
                      />
                      <button className="rf-send act" onClick={() => void sendHelp()} disabled={helpBusy || !helpInput.trim()}>
                        {helpBusy ? <Loader2 className="spin" /> : <Send />}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="rf-btn-row">
              <button className="rf-btn" onClick={() => { haptic('light'); setStep(questions.length ? 'clarify' : 'describe'); }}>Изменить</button>
              <button className="rf-btn primary" onClick={install} disabled={installing}>{installing ? <Loader2 className="spin" /> : <Check />}Создать</button>
            </div>
          </>
        )}

        {!dropOpen && error && <div className="rf-hint" style={errStyle}>{error}</div>}
      </div>
    </div>
  );
}
